<template>
  <div class="container mt-4 mb-5">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h3 class="m-0">Administration: Gruppen</h3>
      <router-link class="btn btn-outline-secondary btn-sm" :to="{ name: 'Invoices' }">Zurück</router-link>
    </div>

    <DefinitionCopyPanel v-if="copySource" :key="copySource.id" title="Gruppe" :source-name="copySource.name"
      :scope="copyScope" assignment-label="Rollen" :assignments="copyAssignments"
      :busy="copyBusy" :error="copyError" @confirm="confirmCopy" @cancel="cancelCopy" />

    <div class="row g-3">
      <div class="col-lg-5">
        <div class="card mb-3">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h5 class="card-title m-0">Gruppen</h5>
              <button class="btn btn-outline-secondary btn-sm" type="button" @click="loadGroups">Neu laden</button>
            </div>

            <div class="mb-3" v-if="userStore.isSystemAdmin">
              <label class="form-label" for="groupCompany">Firma</label>
              <select id="groupCompany" v-model="selectedCompanyId" class="form-select" @change="loadGroups">
                <option value="">Bitte wählen...</option>
                <option v-for="c in companies" :key="c.id" :value="c.id">
                  {{ c.name || c.companyNumber || c.id }}
                </option>
              </select>
            </div>

            <div class="table-responsive">
              <table class="table table-sm table-hover">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th class="text-end">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="g in groups"
                    :key="g.id"
                    :class="{ 'table-active': selectedGroup?.id === g.id }"
                    style="cursor: pointer"
                    @click="selectGroup(g)"
                  >
                    <td>{{ g.name }}</td>
                    <td class="text-end">
                      <button class="btn btn-sm btn-outline-danger" type="button" @click.stop="deleteGroup(g)">Löschen</button>
                      <button v-if="canCopyGroups" class="btn btn-sm btn-outline-secondary ms-1" type="button" :disabled="copyBusy" @click.stop="prepareCopy(g)">Kopieren</button>
                    </td>
                  </tr>
                  <tr v-if="groups.length === 0">
                    <td colspan="2" class="text-center text-muted py-3">Keine Gruppen gefunden.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <hr />

            <h6>Neue Gruppe</h6>
            <div class="row g-2 align-items-end">
              <div class="col-md-6">
                <label class="form-label" for="newGroupName">Name *</label>
                <input id="newGroupName" v-model="createForm.name" class="form-control" type="text" />
              </div>
              <div class="col-md-6">
                <label class="form-label" for="newGroupDesc">Beschreibung</label>
                <input id="newGroupDesc" v-model="createForm.description" class="form-control" type="text" />
              </div>
              <div class="col-md-12">
                <button class="btn btn-primary w-100" type="button" :disabled="!canCreateGroup" @click="createGroup">
                  Erstellen
                </button>
              </div>
              <div class="text-muted small" v-if="userStore.isSystemAdmin && !selectedCompanyId">
                Als <code>SystemAdmin</code> muss eine Firma ausgewählt sein.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-7">
        <div v-if="!selectedGroup" class="alert alert-secondary">
          Wähle links eine Gruppe aus, um Benutzer und Rollen zu verwalten.
        </div>

        <div v-else class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <h5 class="card-title mb-1">{{ selectedGroup.group.name }}</h5>
                <div class="text-muted small">{{ selectedGroup.group.description || "-" }}</div>
              </div>
              <button class="btn btn-outline-secondary btn-sm" type="button" @click="reloadSelectedGroup">Neu laden</button>
            </div>

            <hr />

            <div>
              <h6>Benutzer</h6>
              <div class="row g-2">
                <div class="col-md-6" v-for="u in users" :key="u.id">
                  <div class="form-check">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :id="`guser-${u.id}`"
                      :checked="formUserIds.includes(u.id)"
                      @change="toggleUser(u.id, $event.target.checked)"
                    />
                    <label class="form-check-label" :for="`guser-${u.id}`">
                      {{ u.displayName || u.preferredUserName || u.email || u.id }}
                    </label>
                  </div>
                </div>
              </div>
              <div class="d-flex justify-content-end mt-3">
                <button class="btn btn-primary btn-sm" type="button" :disabled="!usersDirty" @click="saveUsers">
                  Speichern
                </button>
              </div>
            </div>

            <hr />

            <div>
              <h6>Rollen</h6>
              <div class="row g-2">
                <div class="col-md-6" v-for="r in roles" :key="r.id">
                  <div class="form-check">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :id="`grole-${r.id}`"
                      :checked="formRoleIds.includes(r.id)"
                      @change="toggleRole(r.id, $event.target.checked)"
                    />
                    <label class="form-check-label" :for="`grole-${r.id}`">
                      {{ r.name }}
                      <span v-if="r.description" class="text-muted small ms-2">{{ r.description }}</span>
                    </label>
                  </div>
                </div>
              </div>
              <div class="d-flex justify-content-end mt-3">
                <button class="btn btn-primary btn-sm" type="button" :disabled="!rolesDirty" @click="saveRoles">
                  Speichern
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from "vue";
import AdminGroupService from "@/services/AdminGroupService";
import DefinitionCopyPanel from "@/components/DefinitionCopyPanel.vue";
import AdminUserService from "@/services/AdminUserService";
import AdminRoleService from "@/services/AdminRoleService";
import CompanyService from "@/services/CompanyService";
import { useUserStore } from "@/stores/userStore";

const userStore = useUserStore();

const companies = ref([]);
const selectedCompanyId = ref("");

const groups = ref([]);
const selectedGroup = ref(null);

const users = ref([]);
const roles = ref([]);

const GLOBAL_ONLY_ROLE_IDS = new Set([
  "00000000-0000-0000-0000-000000000010",
  "00000000-0000-0000-0000-000000000013",
  "00000000-0000-0000-0000-000000000011",
]);
const GLOBAL_ONLY_ROLE_NAMES = new Set(["systemadmin", "customersupport", "companyadmin"]);

const createForm = reactive({ name: "", description: "" });
const canCopyGroups = computed(() => ["admin.groups.read", "admin.groups.write", "admin.roles.read"].every(p => userStore.hasPermission(p)));
const copySource = ref(null);
const copyAssignments = ref([]);
const copyBusy = ref(false);
const copyError = ref("");
let copyGeneration = 0;
const activeCompanyId = computed(() => userStore.isSystemAdmin ? selectedCompanyId.value : userStore.currentCompanyId);
const copyContext = computed(() => JSON.stringify([userStore.userId, userStore.currentCompanyId, activeCompanyId.value, userStore.isSystemAdmin, userStore.permissions]));
const copyScope = computed(() => {
  const company = [...companies.value, ...userStore.companies].find(c => c.id === copySource.value?.companyId);
  return `Firma der Kopie: ${company?.name || company?.companyNumber || copySource.value?.companyId}.`;
});

function cancelCopy() {
  copyGeneration += 1;
  copySource.value = null;
  copyAssignments.value = [];
  copyError.value = "";
  copyBusy.value = false;
}

watch(copyContext, cancelCopy, { flush: "sync" });

async function prepareCopy(group) {
  if (!canCopyGroups.value || copyBusy.value || group.companyId !== activeCompanyId.value) return;
  cancelCopy();
  const generation = copyGeneration;
  copyBusy.value = true;
  try {
    const response = await AdminGroupService.getGroupById(group.id);
    if (generation !== copyGeneration || response.data.group.companyId !== activeCompanyId.value) return;
    copyAssignments.value = (response.data.roleIds || []).map(id => ({ id, label: roles.value.find(r => r.id === id)?.name || id }));
    copySource.value = response.data.group;
  } finally { if (generation === copyGeneration) copyBusy.value = false; }
}

async function confirmCopy(name) {
  if (!canCopyGroups.value || copyBusy.value || !copySource.value) return;
  if (copySource.value.companyId !== activeCompanyId.value) { cancelCopy(); return; }
  const generation = copyGeneration;
  copyBusy.value = true;
  copyError.value = "";
  try {
    const response = await AdminGroupService.copyGroup(copySource.value.id, name, copyAssignments.value.map(r => r.id));
    if (generation !== copyGeneration) return;
    copySource.value = null;
    await loadGroups();
    if (generation !== copyGeneration) return;
    await selectGroup(response.data);
  } catch (error) {
    if (generation !== copyGeneration) return;
    copyError.value = typeof error.response?.data === "string" ? error.response.data : "Die Gruppe konnte nicht kopiert werden. Bitte Quelle und Berechtigungen erneut prüfen.";
  } finally { if (generation === copyGeneration) copyBusy.value = false; }
}

const formUserIds = reactive([]);
const formRoleIds = reactive([]);

const original = reactive({ userIds: [], roleIds: [] });

const canCreateGroup = computed(() => {
  if (!createForm.name) return false;
  if (userStore.isSystemAdmin) return Boolean(selectedCompanyId.value);
  return true;
});

function isCompanyAssignableRoleId(roleId) {
  return !GLOBAL_ONLY_ROLE_IDS.has(String(roleId || "").toLowerCase());
}

function isCompanyAssignableRole(role) {
  return isCompanyAssignableRoleId(role?.id)
    && !GLOBAL_ONLY_ROLE_NAMES.has(String(role?.name || "").toLowerCase());
}

function snapshotOriginal() {
  original.userIds = [...formUserIds];
  original.roleIds = [...formRoleIds];
}

const usersDirty = computed(() => {
  const a = [...formUserIds].sort().join(",");
  const b = [...original.userIds].sort().join(",");
  return a !== b;
});

const rolesDirty = computed(() => {
  const a = [...formRoleIds].sort().join(",");
  const b = [...original.roleIds].sort().join(",");
  return a !== b;
});

function toggleUser(id, checked) {
  const idx = formUserIds.indexOf(id);
  if (checked && idx === -1) formUserIds.push(id);
  if (!checked && idx !== -1) formUserIds.splice(idx, 1);
}

function toggleRole(id, checked) {
  const idx = formRoleIds.indexOf(id);
  if (checked && idx === -1) formRoleIds.push(id);
  if (!checked && idx !== -1) formRoleIds.splice(idx, 1);
}

async function loadSupportingData() {
  const rolesResp = await AdminRoleService.getRoles();
  roles.value = Array.isArray(rolesResp?.data)
    ? rolesResp.data.filter(isCompanyAssignableRole)
    : [];

  if (userStore.isSystemAdmin) {
    const companiesResp = await CompanyService.getAll(null, 1, 200);
    companies.value = Array.isArray(companiesResp?.data) ? companiesResp.data : [];
    if (!selectedCompanyId.value && userStore.currentCompanyId) {
      selectedCompanyId.value = userStore.currentCompanyId;
    }
  }
}

async function loadUsers() {
  // CompanyAdmin: API filters by current company automatically.
  const response = await AdminUserService.getUsers(null, 1, 500);
  users.value = Array.isArray(response?.data) ? response.data : [];
}

async function loadGroups() {
  const context = copyContext.value;
  const companyId = userStore.isSystemAdmin ? (selectedCompanyId.value || null) : null;
  const response = await AdminGroupService.getGroups(companyId);
  if (context !== copyContext.value) return;
  groups.value = Array.isArray(response?.data) ? response.data : [];
  if (selectedGroup.value?.group?.id) {
    const stillExists = groups.value.some((g) => g.id === selectedGroup.value.group.id);
    if (!stillExists) {
      selectedGroup.value = null;
    }
  }
}

async function selectGroup(group) {
  const context = copyContext.value;
  const response = await AdminGroupService.getGroupById(group.id);
  if (context !== copyContext.value) return;
  selectedGroup.value = response?.data || null;
  formUserIds.splice(0, formUserIds.length, ...(selectedGroup.value?.userIds || []));
  formRoleIds.splice(0, formRoleIds.length, ...(selectedGroup.value?.roleIds || []).filter(isCompanyAssignableRoleId));
  snapshotOriginal();
}

async function reloadSelectedGroup() {
  if (!selectedGroup.value?.group?.id) return;
  const response = await AdminGroupService.getGroupById(selectedGroup.value.group.id);
  selectedGroup.value = response?.data || null;
  formUserIds.splice(0, formUserIds.length, ...(selectedGroup.value?.userIds || []));
  formRoleIds.splice(0, formRoleIds.length, ...(selectedGroup.value?.roleIds || []).filter(isCompanyAssignableRoleId));
  snapshotOriginal();
}

async function saveUsers() {
  if (!selectedGroup.value?.group?.id) return;
  await AdminGroupService.setGroupUsers(selectedGroup.value.group.id, formUserIds);
  await reloadSelectedGroup();
}

async function saveRoles() {
  if (!selectedGroup.value?.group?.id) return;
  await AdminGroupService.setGroupRoles(selectedGroup.value.group.id, formRoleIds);
  await reloadSelectedGroup();
}

async function createGroup() {
  const payload = {
    companyId: userStore.isSystemAdmin ? selectedCompanyId.value : null,
    name: createForm.name,
    description: createForm.description || null,
  };
  await AdminGroupService.createGroup(payload);
  createForm.name = "";
  createForm.description = "";
  await loadGroups();
}

async function deleteGroup(group) {
  const confirmed = window.confirm(`Gruppe "${group.name}" wirklich löschen?`);
  if (!confirmed) return;
  await AdminGroupService.deleteGroup(group.id);
  await loadGroups();
}

onMounted(async () => {
  if (!userStore.me) {
    try {
      await userStore.loadMe();
    } catch (e) {
      console.error("Failed to load /user/me:", e);
    }
  }

  await loadSupportingData();
  await loadUsers();
  await loadGroups();
});
</script>
